import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { cn } from 'ui/lib/utils';
import { UIChip } from 'ui/chip';
import { UIProductCardSkeleton } from 'blocks/product-card';
import { UIRollingReviews } from 'blocks/rolling-reviews';
import { getCategories, CategoryDocument } from '@util/firestore/categories';
import { ProductDocument } from '@util/types/firestore/products';
import { ROLLING_REVIEWS } from '@util/firestore/reviews';
import { useSectionViewTracking } from '@util/analytics/sectionTracking';
import MarketplaceProductCard from './MarketplaceProductCard';
import { applyFilter, FILTERS } from './featuredListings.filters';

// Sizing is in explicit px: gearfocus-web sets a 10px rem root, so Tailwind's
// rem-based scale (text-3xl, py-16, ...) would render at 62.5%.

interface FeaturedListingsProps {
    items: ProductDocument[];
    loading?: boolean;
}


// One container, two layouts: a swipeable horizontal row on mobile, a grid from
// md up. Same DOM for both (so each product's JSON-LD renders once, not twice),
// switched by CSS. The negative margin + padding lets the mobile row bleed to
// the screen edges inside the section's px-[16px]. Scrollbar hidden.
//
// min-h-[440px] (mobile only, reset at md where this becomes a wrapping grid
// instead of a single stretched flex row): on mobile this is one flex row, so
// its height is the TALLEST card, not the average — a card carrying an extra
// element the skeleton doesn't model (an urgency badge, a metaRow) sets that
// max. Measured on the live preview: skeleton cards render at 370px, but real
// cards range 393-421px depending on what each item actually has, so the row
// grows ~50px when skeleton swaps to real content regardless of any single
// card's title length (GB perf fix; this row-level reservation replaced an
// earlier per-title fix in ui-product-card.module.scss that measured zero
// effect on CLS, because it could only affect the row's minimum, not its max).
// min-h-[264px] = the original measured 440px floor scaled by the same 0.6
// zoom applied to the cards below (Bill, 2026-08-21: mobile cards were "way
// too big," cut to 60%) — the row's height reservation has to shrink with
// the cards it's reserving space for, or it leaves a dead gap under them.
const FEATURED_CONTAINER =
    '-mx-[16px] flex min-h-[264px] snap-x gap-[16px] overflow-x-auto px-[16px] pb-[8px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:min-h-0 md:grid md:grid-cols-3 md:overflow-visible md:px-0 md:pb-0 lg:grid-cols-4 xl:grid-cols-5';

// Fixed card width for the mobile scroll row (≈1.6 cards in view hints the
// swipe); grid tracks take over from md up.
//
// zoom (not transform: scale) below md, reset to 1 at md: zoom shrinks the
// element's own contribution to its parent's layout along with its visual
// size, so the row packs tighter instead of leaving each card's original
// footprint behind it — scale only changes the paint, not the layout box,
// which would have left 240px-wide gaps around 144px-looking cards.
const CARD_SIZING =
    'w-[62vw] max-w-[240px] shrink-0 snap-start [zoom:0.6] md:w-auto md:max-w-none md:[zoom:1]';

/**
 * Mobile shows all 12, swipeable. Desktop stays exactly two rows by hiding the
 * overflow per breakpoint: 6 from md (3×2), 8 from lg (4×2), 10 from xl (5×2).
 * `md:hidden` cascades up (hidden md→xl unless a larger breakpoint re-shows it),
 * so a card visible only on mobile just needs `md:hidden`.
 */
const CARD_VISIBILITY = [
    '', '', '', '', '', '',
    'md:hidden lg:block', 'md:hidden lg:block',
    'md:hidden lg:hidden xl:block', 'md:hidden lg:hidden xl:block',
    'md:hidden', 'md:hidden',
];
const MAX_CARDS = CARD_VISIBILITY.length;

export default function FeaturedListings({
    items,
    loading = false,
}: FeaturedListingsProps) {
    const [activeKey, setActiveKey] = useState('all');
    // null until the category lookup settles (either outcome).
    const [nameBySlug, setNameBySlug] = useState<Record<string, string> | null>(null);

    // Products store their category ancestry as NAMES, so resolve each chip's
    // slug to its level-1 category name (same lookup CategoryNav does).
    useEffect(() => {
        let cancelled = false;
        getCategories(2)
            .then((all: CategoryDocument[]) => {
                if (cancelled) return;
                const map: Record<string, string> = {};
                for (const f of FILTERS) {
                    if (f.kind !== 'category') continue;
                    const l1 = all.find((c) => c.slug === f.slug && c.level === 1);
                    if (l1) map[f.slug] = l1.name;
                }
                setNameBySlug(map);
            })
            .catch(() => {
                // Settle with an empty map so category chips degrade to showing
                // everything rather than spinning on skeletons forever.
                if (!cancelled) setNameBySlug({});
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const activeFilter = FILTERS.find((f) => f.key === activeKey);

    // A category chip can't be applied until the slug→name lookup resolves.
    const awaitingCategories =
        activeFilter?.kind === 'category' && nameBySlug === null;
    const showSkeletons = loading || awaitingCategories;

    const visible = useMemo(
        () => applyFilter(items, activeFilter, nameBySlug),
        [items, activeFilter, nameBySlug]
    );

    // Placement of the reviews widget relative to the grid is an open
    // question (Bill, 2026-08-21 — CPO/CMO gave opposing confidence
    // intervals, both citing missing reach data as the blocker). These fire
    // once per session and answer it directly: what fraction of sessions
    // actually scroll far enough to see the grid vs the reviews widget below
    // it. Check GA4 `section_view` by `section` 3+ days after ship.
    const { ref: gridRef } = useSectionViewTracking<HTMLDivElement>('homepage_featured_grid');
    const { ref: reviewsRef } =
        useSectionViewTracking<HTMLDivElement>('homepage_rolling_reviews');

    return (
        <section id="featured" className="bg-gray-50">
            {/* pb-0 at every width (Bill, 2026-08-22 — was sm:pb-[64px] on
                desktop, which stacked with TrustedBrands' own sm:pt-[32px] to
                make a 96px reviews-to-brands gap against a 32px grid-to-
                reviews gap). TrustedBrands' own top padding is now the SOLE
                source of this gap at every breakpoint — matches the mobile
                pattern (pb-0, gap comes from pt-[40px] on TrustedBrands) below
                sm, and gap-[32px] at every gap above sm. */}
            <div className="mx-auto max-w-[1280px] px-[16px] pt-[24px] pb-0">
                <div className="mb-[32px] flex flex-wrap items-end justify-between gap-[16px]">
                    <div className="max-w-[640px]">
                        <h2 className="text-[2.4rem] font-semibold text-dark sm:text-[3.2rem]">
                            Featured Gear
                        </h2>
                        {/* Below sm (Bill, 2026-08-21): "View all listings" moves
                            onto the same line as the subtitle instead of
                            wrapping to its own row below both. The sm+ copy of
                            the link (below, outside this div) stays in its
                            original spot next to the heading block — this one
                            is hidden from sm up so only one is ever visible. */}
                        <div className="mt-[8px] flex items-center justify-between gap-[16px] sm:block">
                            <p className="text-[16px] text-gray-600">
                                Listings from verified sellers, curated by our team.
                            </p>
                            <Link
                                href="/collections/featured"
                                title="Browse Our Featured New & Used Cameras And Lenses"
                                className="inline-flex shrink-0 items-center gap-[4px] text-[14px] font-semibold text-primary transition-all hover:gap-[8px] sm:hidden"
                            >
                                View all listings
                                <ArrowRight aria-hidden="true" className="size-[16px]" />
                            </Link>
                        </div>
                    </div>
                    <Link
                        href="/collections/featured"
                        title="Browse Our Featured New & Used Cameras And Lenses"
                        className="hidden items-center gap-[4px] text-[14px] font-semibold text-primary transition-all hover:gap-[8px] sm:inline-flex"
                    >
                        View all listings
                        <ArrowRight aria-hidden="true" className="size-[16px]" />
                    </Link>
                </div>

                <div className="mb-[24px] flex flex-wrap justify-center gap-[8px] sm:justify-start">
                    {FILTERS.map((f) => (
                        <UIChip
                            key={f.key}
                            active={f.key === activeKey}
                            onClick={() => setActiveKey(f.key)}
                        >
                            {f.label}
                        </UIChip>
                    ))}
                </div>

                <div ref={gridRef}>
                    {showSkeletons ? (
                        <div
                            aria-busy="true"
                            aria-label="Loading featured listings"
                            className={FEATURED_CONTAINER}
                        >
                            {/* Visibility goes on a wrapper, not the skeleton: its
                                SCSS-module `display: flex` is unlayered and would
                                beat Tailwind's layered `hidden`. */}
                            {CARD_VISIBILITY.map((visibility, i) => (
                                <div key={i} className={cn(CARD_SIZING, visibility)}>
                                    <UIProductCardSkeleton />
                                </div>
                            ))}
                        </div>
                    ) : visible.length > 0 ? (
                        <div className={FEATURED_CONTAINER}>
                            {visible.slice(0, MAX_CARDS).map((item, i) => (
                                <MarketplaceProductCard
                                    // id, not sku: ~40% of listings carry an empty
                                    // sku, so keying on it collides and lets React
                                    // swap card instances (stale wishlist state) when
                                    // a filter chip re-renders the grid.
                                    key={item.id}
                                    item={item}
                                    className={cn(CARD_SIZING, CARD_VISIBILITY[i])}
                                    // First row is above the fold; let it be LCP.
                                    priority={i < 2}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="py-[32px] text-center text-[14px] text-gray-600">
                            No featured listings match this filter right now.
                        </p>
                    )}
                </div>

                <div ref={reviewsRef}>
                    <UIRollingReviews
                        reviews={ROLLING_REVIEWS}
                        className="mt-[40px] sm:mt-[32px]"
                    />
                </div>
            </div>
        </section>
    );
}
